import { init } from '@module-federation/enhanced/runtime';
import { Remote } from '@module-federation/runtime-core/types';
import type {
  SharedFunction,
  SharedLibraryConfig,
} from '@nx/module-federation';
import { EAGER, SINGLETON } from './consts';

export interface RemoteModuleConfig {
  googleAnalyticsId?: string;
}

export type RemoteModuleEntry<
  C extends RemoteModuleConfig & Record<string, unknown>,
> = Remote & {
  remotePublicUrl: string;
  config: C;
};

export interface ModuleFederationManifest {
  [key: string]: RemoteModuleEntry<any>;
}

export const PALDESK_MANIFEST = '__paldesk_manifest__';

export function getPaldeskManifest(): ModuleFederationManifest | undefined {
  return (window as any)[PALDESK_MANIFEST];
}

export async function replaceEnvironmentFile(path: string, target: any) {
  try {
    const response = await fetch(`${path}?timestamp=${Date.now()}`);
    const data = await response.json();
    Object.assign(target, data);
  } catch (error) {
    console.error('Error replacing environment file:', error);
  }
}

export async function prepareHostForModuleFederation(options: {
  hostName: string;
  env: string;
}) {
  const { hostName, env } = options;
  try {
    let manifestUrl: string;

    if (env === 'e2e' || env === 'mfe-local') {
      manifestUrl = '/remote-manifest.local.json';
    } else if (env) {
      // HACK: module federation not working without ngDevMode. I don't know why it is missing but this is a workaround until it will be properly fixed by nx angular or our team
      (window as any)['ngDevMode'] = false;
      manifestUrl = `https://static2-${env}.paldesk.palfinger.com/mfe/remote-manifest.json?timestamp=${Date.now()}`;
    } else {
      manifestUrl = '/remote-manifest.json';
    }

    const manifestRes = await fetch(manifestUrl);
    const manifest: ModuleFederationManifest = await manifestRes.json();
    (window as any)[PALDESK_MANIFEST] = manifest;

    const config = {
      name: hostName,
      remotes: Object.entries(manifest).map(([key, value]) => ({
        ...value,
        name: key,
        entry: `${(value as { entry: string }).entry}?timestamp=${Date.now()}`,
      })),
    };

    return init(config);
  } catch (error) {
    console.error('Error preparing host for module federation:', error);
    throw error;
  }
}

export function createPaldeskSharedConfig(
  override?: (name: string) => SharedLibraryConfig | undefined | false,
): SharedFunction {
  return (name: string) => {
    const isEager = EAGER.some((pkg) => name?.startsWith(pkg));
    const isSingleton = SINGLETON.some((pkg) => name?.startsWith(pkg));

    if (isEager || isSingleton) {
      return {
        singleton: isSingleton,
        strictVersion: false,
        requiredVersion: false,
        eager: isEager,
      };
    }

    const overrideResult = override?.(name);

    if (overrideResult !== undefined) return overrideResult;

    return false;
  };
}
